import { type MemoClassification } from '@kalos/kalos-rpc';
import { Button, cn, FileUploader, Textarea, toast } from '@kalos/ui';
import { Check, ChevronDown, Mic, Plus, Square } from 'lucide-react';
import { type FC, useEffect, useId, useState } from 'react';

import { DialogOrSheet } from '../../../../../components/DialogOrSheet';
import { JobSelector } from '../../../../../components/JobSelector';
import {
  FILE_UPLOAD_ACCEPT,
  MAX_FILE_SIZE_BYTES,
  MAX_FILES,
  validateFileSizes,
} from '../../../../../tools/fileUploadUtils';
import { formatDurationFromSeconds } from '../../../../../tools/formatTimeUtils';
import { AudioNoteRow, MicPermissionDialog } from '../NotesForm/AudioRecorder';
import { useAudioRecorder } from '../NotesForm/useAudioRecorder';
import { useSubmitJobNote } from '../NotesForm/useSubmitJobNote';

/** memo_classification 1 is "None", the category a note gets when nobody picks one. */
const NO_CATEGORY_ID = 1;

export interface NoteComposerProps {
  /**
   * The job (event) the note is saved to. Leave it out where there's no job yet (all notes): the
   * composer then shows a Job field, and Save asks for a job if none is chosen.
   */
  jobId?: number;
  loggedUserId: number;
  categories: MemoClassification[];
  /** Fixes the category, as the daily report does; the pill shows it but can't change it. */
  lockedCategoryId?: number;
  /** Fires after a note is saved. */
  onSaved?: () => void | Promise<void>;
  /** Tells a surrounding sheet when a save is running, so it can't be closed mid-upload. */
  onBusyChange?: (isBusy: boolean) => void;
}

/**
 * The phone note composer from the field view prototype: a one-line note field, then "+" for
 * attachments, one category pill and Save. Notes are internal and unpinned, as the note form
 * defaults them; pinning and visibility are edited on the note afterwards.
 */
export const NoteComposer: FC<NoteComposerProps> = ({
  jobId,
  loggedUserId,
  categories,
  lockedCategoryId,
  onSaved,
  onBusyChange,
}) => {
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState(lockedCategoryId ?? NO_CATEGORY_ID);
  const [files, setFiles] = useState<File[]>([]);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioSeconds, setAudioSeconds] = useState<number | undefined>(undefined);
  const [isAttachOpen, setIsAttachOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [chosenJobId, setChosenJobId] = useState(0);
  const [jobError, setJobError] = useState<string | null>(null);
  const jobFieldId = useId();
  const jobErrorId = useId();
  const saveToJobId = jobId ?? chosenJobId;

  const recorder = useAudioRecorder({
    onComplete: (blob, seconds) => {
      setAudioBlob(blob);
      setAudioSeconds(seconds);
    },
  });
  const { submitNote, isPending, isUploadingLargeVideos, isUpdatingMemo } = useSubmitJobNote();

  const isBusy = isSaving || isPending || isUploadingLargeVideos || isUpdatingMemo;
  useEffect(() => onBusyChange?.(isBusy), [isBusy, onBusyChange]);
  const fileSizeCheck = files.length ? validateFileSizes(files) : { isValid: true, error: null };
  const hasContent = description.trim().length > 0 || files.length > 0 || !!audioBlob;
  const effectiveCategoryId = lockedCategoryId ?? categoryId;
  const categoryName = categories.find((c) => c.id === effectiveCategoryId)?.categoryDescription;
  const isUncategorised = effectiveCategoryId === NO_CATEGORY_ID || !categoryName;

  const save = async () => {
    if (!hasContent || isBusy || recorder.isRecording) return;
    if (!saveToJobId) {
      // Say what's missing rather than disabling Save.
      setJobError('Choose the job this note is for.');
      return;
    }
    setIsSaving(true);
    try {
      await submitNote({
        description,
        classificationId: effectiveCategoryId,
        files,
        audioBlob,
        jobId: saveToJobId,
        loggedUserId,
      });
      setDescription('');
      setFiles([]);
      setAudioBlob(null);
      setAudioSeconds(undefined);
      if (lockedCategoryId === undefined) setCategoryId(NO_CATEGORY_ID);
      toast({ title: 'Note saved', variant: 'success' });
      await onSaved?.();
    } catch (error) {
      console.error('Failed to save note:', error);
      toast({
        title: 'Note not saved',
        description: 'Try again. If you attached large files, try fewer at a time.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const attachmentSummary = files.length
    ? `${files.length} ${files.length === 1 ? 'file' : 'files'} attached`
    : null;

  return (
    <div className="flex flex-col gap-2">
      {jobId === undefined && (
        // All notes: the note's job, kept after a save for the next note on the same job.
        <div className="flex flex-col gap-1">
          <label htmlFor={jobFieldId} className="text-xs font-medium">
            Job
          </label>
          <JobSelector
            id={jobFieldId}
            value={chosenJobId}
            onChange={(value: number) => {
              setChosenJobId(value || 0);
              if (value) setJobError(null);
            }}
            disabled={isBusy}
          />
          {jobError && (
            <p id={jobErrorId} role="alert" className="text-danger-foreground text-xs font-medium">
              {jobError}
            </p>
          )}
        </div>
      )}
      <Textarea
        aria-label="Note"
        placeholder="Type a note..."
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        autoGrow={{ minRows: 1, maxRows: 8 }}
        maxLength={5000}
        disabled={isBusy}
        // On touch: 16px text like the other fields (it stops iOS zooming in), and padding that
        // makes one line 46px. A min-height here would throw off the auto-grow's row count.
        className="bg-card touch:py-2.5 touch:text-base"
      />

      {recorder.isRecording && (
        <div className="flex items-center justify-between gap-2" role="status">
          <span className="text-danger-foreground text-sm font-medium tabular-nums">
            Recording {formatDurationFromSeconds(recorder.elapsedSeconds)}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={recorder.stop}
          >
            <Square className="h-4 w-4" aria-hidden />
            Stop recording
          </Button>
        </div>
      )}
      {!!audioBlob && (
        <AudioNoteRow
          audioBlob={audioBlob}
          durationSeconds={audioSeconds}
          disabled={isBusy}
          onClear={() => {
            setAudioBlob(null);
            setAudioSeconds(undefined);
          }}
        />
      )}
      {attachmentSummary && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-muted-foreground text-sm">{attachmentSummary}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={isBusy}
            onClick={() => setFiles([])}
          >
            Remove
          </Button>
        </div>
      )}
      {!fileSizeCheck.isValid && (
        <p className="text-destructive text-sm" role="alert">
          {fileSizeCheck.error}
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          className="shrink-0 gap-1"
          disabled={isBusy || recorder.isRecording}
          onClick={() => setIsAttachOpen(true)}
        >
          <Plus className="h-4 w-4" aria-hidden />
          Attach
        </Button>
        {lockedCategoryId !== undefined ? (
          <span className="bg-muted text-foreground touch:min-h-11 flex min-h-9 min-w-0 flex-1 items-center gap-2 rounded-md px-3 text-sm">
            <span className="truncate">{categoryName ?? 'Category'}</span>
            <span className="text-muted-foreground text-xs">Locked</span>
          </span>
        ) : (
          <Button
            type="button"
            variant="ghost"
            className="bg-muted hover:bg-accent min-w-0 flex-1 justify-between gap-1"
            disabled={isBusy}
            onClick={() => setIsCategoryOpen(true)}
          >
            <span className={cn('truncate', isUncategorised && 'text-muted-foreground')}>
              {isUncategorised ? 'Which category?' : categoryName}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
          </Button>
        )}
        <Button
          type="button"
          className="shrink-0"
          disabled={!hasContent || isBusy || recorder.isRecording || !fileSizeCheck.isValid}
          isLoading={isBusy}
          onClick={() => void save()}
        >
          Save
        </Button>
      </div>

      <DialogOrSheet
        open={isAttachOpen}
        onOpenChange={setIsAttachOpen}
        title="Add to note"
        description="Take a photo, choose files, or record an audio note."
      >
        <div className="flex flex-col gap-4">
          <FileUploader
            value={files}
            onValueChange={(next) => setFiles(typeof next === 'function' ? next(files) : next)}
            accept={FILE_UPLOAD_ACCEPT}
            maxFiles={MAX_FILES}
            maxSize={MAX_FILE_SIZE_BYTES}
            multiple
            useButtons
          />
          <Button
            type="button"
            variant="outline"
            className="w-full gap-2"
            disabled={!!audioBlob || recorder.isStarting}
            onClick={() => {
              setIsAttachOpen(false);
              void recorder.start();
            }}
          >
            <Mic className="h-4 w-4" aria-hidden />
            {audioBlob ? 'Audio note added' : 'Record audio note'}
          </Button>
          <Button type="button" className="w-full" onClick={() => setIsAttachOpen(false)}>
            Done
          </Button>
        </div>
      </DialogOrSheet>

      <DialogOrSheet open={isCategoryOpen} onOpenChange={setIsCategoryOpen} title="Note category">
        <ul aria-label="Categories" className="divide-border -mx-1 divide-y">
          {categories.map((category) => {
            const isSelected = category.id === categoryId;
            return (
              <li key={category.id}>
                <button
                  type="button"
                  aria-pressed={isSelected}
                  className="hover:bg-accent flex min-h-11 w-full items-center justify-between gap-2 rounded-md px-1 text-left text-sm"
                  onClick={() => {
                    setCategoryId(category.id);
                    setIsCategoryOpen(false);
                  }}
                >
                  {category.id === NO_CATEGORY_ID ? 'No category' : category.categoryDescription}
                  {isSelected && <Check className="h-4 w-4 shrink-0" aria-hidden />}
                </button>
              </li>
            );
          })}
        </ul>
      </DialogOrSheet>

      <MicPermissionDialog
        message={recorder.permissionError}
        onClose={recorder.dismissPermissionError}
      />
    </div>
  );
};
